/* ostire-scrub.js — scroll-driven video hero.

   Scroll position across the section maps onto the clip's timeline. The clip
   is fetched whole and handed to the <video> as an object URL: seeking a
   network-backed video issues a byte-range request per seek and stutters
   badly, whereas a local blob seeks instantly. The cost is that nothing moves
   until the download finishes, which is why the poster stays up until the
   first frame is actually painted.

   Self-initialising: scans for [data-os-scrub] on load and again whenever the
   theme editor re-renders a section. */
(function () {
  'use strict';

  function clamp(n, lo, hi) { return n < lo ? lo : n > hi ? hi : n; }

  function setup(root) {
    if (!root || root.dataset.scrubReady === '1') return;
    root.dataset.scrubReady = '1';

    var video = root.querySelector('.os-scrub__video');
    var bands = [].slice.call(root.querySelectorAll('[data-scrub-band]'));
    var jumps = [].slice.call(root.querySelectorAll('[data-scrub-jump]'));
    if (!bands.length) return;

    var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var mobileQ = window.matchMedia('(max-width: 860px), (hover: none) and (pointer: coarse)');

    var rootTop = 0, total = 1, vh = window.innerHeight;
    var dirty = true, frame = 0, destroyed = false;
    var current = 0, target = 0, active = -1;
    var ready = false, loading = false, failed = false;
    var objectUrl = null, loadedSrc = null, unlocked = false;

    function isMobile() { return mobileQ.matches; }

    function sourceFor() {
      if (isMobile()) {
        if (root.dataset.mobileVideo === 'off') return '';
        return root.dataset.videoMobile || '';
      }
      return root.dataset.videoDesktop || '';
    }

    function layout() {
      var pageY = window.scrollY || window.pageYOffset;
      rootTop = root.getBoundingClientRect().top + pageY;
      vh = window.innerHeight;
      // The sticky stage is pinned for the section's height minus one
      // viewport, so that range is what maps onto the clip.
      total = Math.max(root.offsetHeight - vh, 1);
      dirty = true;
    }

    function loadClip() {
      var src = sourceFor();
      if (reduce || destroyed || loading || failed || !src) return;
      if (ready && loadedSrc === src) return;

      loading = true;
      loadedSrc = src;

      fetch(src, { credentials: 'omit' })
        .then(function (res) {
          if (!res.ok) throw new Error('clip ' + res.status);
          return res.blob();
        })
        .then(function (blob) {
          if (destroyed || loadedSrc !== src) return;
          if (objectUrl) URL.revokeObjectURL(objectUrl);
          objectUrl = URL.createObjectURL(blob);

          function onReady() {
            if (ready) return;
            ready = true;
            loading = false;
            dirty = true;
            if (unlocked) prime();
          }

          video.addEventListener('loadedmetadata', onReady);
          // Belt and braces: some browsers settle on loadeddata first.
          video.addEventListener('loadeddata', onReady);
          video.addEventListener('seeked', function () {
            root.dataset.videoPainted = 'true';
          }, { once: true });
          video.addEventListener('error', function () {
            failed = true; ready = false; loading = false;
            delete root.dataset.videoPainted;
          }, { once: true });

          // preload="none" in the markup keeps the browser from touching the
          // network before this script decides to. It must be raised before
          // assigning src, or load() selects the resource and then stops
          // without ever fetching metadata — readyState stays at 0 forever.
          video.preload = 'auto';
          video.src = objectUrl;
          video.load();
        })
        .catch(function () {
          // Poster stays on screen; the hero is still a complete composition.
          failed = true;
          loading = false;
        });
    }

    // iOS will not paint a frame from a seek until the element has played once
    // inside a user gesture. Play-then-pause on first touch unlocks it.
    function prime() {
      if (!video || !isMobile()) return;
      try {
        var p = video.play();
        if (p && p.then) p.then(function () { video.pause(); }).catch(function () {});
        else video.pause();
      } catch (e) {}
    }

    function readScroll() {
      var pageY = window.scrollY || window.pageYOffset;
      var y = clamp(pageY - rootTop, 0, total);
      target = y / total;
      root.style.setProperty('--ss-progress', String(target));

      // Active chapter: the last band whose top has crossed the middle of the
      // viewport. Measured live rather than from offsetTop, because the bands
      // sit in a negative-margin stack and their pins are sticky.
      var idx = 0, i;
      for (i = 0; i < bands.length; i++) {
        if (bands[i].getBoundingClientRect().top <= vh * 0.5) idx = i;
      }
      if (idx !== active) {
        active = idx;
        for (i = 0; i < bands.length; i++) {
          if (i === active) bands[i].setAttribute('data-active', 'true');
          else bands[i].removeAttribute('data-active');
        }
        for (i = 0; i < jumps.length; i++) {
          if (i === active) jumps[i].setAttribute('aria-current', 'step');
          else jumps[i].removeAttribute('aria-current');
        }
      }
    }

    function updateVideo() {
      if (!ready || !video || video.seeking) return;
      if (Math.abs(current - target) < 0.0004) return;
      current += (target - current) * 0.2;
      var dur = video.duration;
      if (!dur || !isFinite(dur)) return;
      var t = clamp(current, 0, 0.999) * dur;
      var eps = isMobile() ? 0.02 : 0.008;
      if (Math.abs(video.currentTime - t) > eps) {
        try { video.currentTime = t; } catch (e) {}
      }
    }

    function tick() {
      if (destroyed) return;
      if (dirty) { dirty = false; readScroll(); }
      updateVideo();
      frame = window.requestAnimationFrame(tick);
    }

    function onScroll() { dirty = true; }
    function onGesture() {
      if (unlocked) return;
      unlocked = true;
      if (ready) prime();
    }

    jumps.forEach(function (btn, i) {
      btn.addEventListener('click', function () {
        var b = bands[i];
        if (!b) return;
        window.scrollTo({
          top: rootTop + b.offsetTop + b.offsetHeight * 0.15,
          behavior: reduce ? 'auto' : 'smooth'
        });
      });
    });

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', layout);
    window.addEventListener('orientationchange', layout);
    window.addEventListener('load', layout);
    window.addEventListener('pointerdown', onGesture, { once: true, passive: true });
    window.addEventListener('touchstart', onGesture, { once: true, passive: true });

    layout();
    // Fonts and the poster both change layout height, and therefore where
    // every band starts, so re-measure once they have settled.
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(layout);

    frame = window.requestAnimationFrame(tick);
    loadClip();

    root.__scrubDestroy = function () {
      destroyed = true;
      window.cancelAnimationFrame(frame);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', layout);
      window.removeEventListener('orientationchange', layout);
      window.removeEventListener('load', layout);
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }

  function scan(scope) {
    var roots = (scope || document).querySelectorAll('[data-os-scrub]');
    [].forEach.call(roots, setup);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { scan(); });
  } else {
    scan();
  }

  document.addEventListener('shopify:section:load', function (e) { scan(e.target); });
  document.addEventListener('shopify:section:unload', function (e) {
    var r = e.target && e.target.querySelector('[data-os-scrub]');
    if (r && r.__scrubDestroy) r.__scrubDestroy();
  });
})();
